import { createContext, type ReactNode, useCallback, useContext, useMemo, useReducer } from 'react'

interface OnboardingState {
	completedTours: string[]
	skippedTours: string[]
	activeTour: string | null
	currentStepIndex: number
	hasSeenWelcome: boolean
}

type OnboardingAction =
	| { type: 'START_TOUR'; tourId: string }
	| { type: 'NEXT_STEP' }
	| { type: 'PREV_STEP' }
	| { type: 'SKIP_TOUR' }
	| { type: 'COMPLETE_TOUR' }
	| { type: 'MARK_WELCOME_SEEN' }
	| { type: 'RESET_ALL' }

// Use getStoredValue/storeValue pattern from SettingsContext
function getStoredValue<T>(key: string, defaultValue: T): T {
	if (typeof window === 'undefined') return defaultValue
	try {
		const stored = localStorage.getItem(key)
		return stored ? (JSON.parse(stored) as T) : defaultValue
	} catch {
		return defaultValue
	}
}

function storeValue<T>(key: string, value: T): void {
	try {
		localStorage.setItem(key, JSON.stringify(value))
	} catch {
		// Silently ignore localStorage errors (e.g., quota exceeded, private browsing)
	}
}

const STORAGE_KEYS = {
	completed: 'playsmith-onboarding-completed',
	skipped: 'playsmith-onboarding-skipped',
	welcome: 'playsmith-onboarding-welcome',
}

function getInitialState(): OnboardingState {
	return {
		completedTours: getStoredValue<string[]>(STORAGE_KEYS.completed, []),
		skippedTours: getStoredValue<string[]>(STORAGE_KEYS.skipped, []),
		activeTour: null,
		currentStepIndex: 0,
		hasSeenWelcome: getStoredValue<boolean>(STORAGE_KEYS.welcome, false),
	}
}

function reducer(state: OnboardingState, action: OnboardingAction): OnboardingState {
	switch (action.type) {
		case 'START_TOUR':
			return { ...state, activeTour: action.tourId, currentStepIndex: 0 }
		case 'NEXT_STEP':
			return { ...state, currentStepIndex: state.currentStepIndex + 1 }
		case 'PREV_STEP':
			return { ...state, currentStepIndex: Math.max(0, state.currentStepIndex - 1) }
		case 'SKIP_TOUR': {
			const skipped = state.activeTour
				? [...state.skippedTours, state.activeTour]
				: state.skippedTours
			storeValue(STORAGE_KEYS.skipped, skipped)
			return { ...state, activeTour: null, currentStepIndex: 0, skippedTours: skipped }
		}
		case 'COMPLETE_TOUR': {
			const completed = state.activeTour
				? [...state.completedTours, state.activeTour]
				: state.completedTours
			storeValue(STORAGE_KEYS.completed, completed)
			return { ...state, activeTour: null, currentStepIndex: 0, completedTours: completed }
		}
		case 'MARK_WELCOME_SEEN':
			storeValue(STORAGE_KEYS.welcome, true)
			return { ...state, hasSeenWelcome: true }
		case 'RESET_ALL':
			localStorage.removeItem(STORAGE_KEYS.completed)
			localStorage.removeItem(STORAGE_KEYS.skipped)
			localStorage.removeItem(STORAGE_KEYS.welcome)
			return { ...getInitialState(), completedTours: [], skippedTours: [], hasSeenWelcome: false }
		default:
			return state
	}
}

interface OnboardingContextType {
	state: OnboardingState
	startTour: (tourId: string) => void
	nextStep: () => void
	prevStep: () => void
	skipTour: () => void
	completeTour: () => void
	markWelcomeSeen: () => void
	resetAllTours: () => void
	isCompleted: (tourId: string) => boolean
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined)

export function OnboardingProvider({ children }: { children: ReactNode }) {
	const [state, dispatch] = useReducer(reducer, null, getInitialState)

	const startTour = useCallback((tourId: string) => dispatch({ type: 'START_TOUR', tourId }), [])
	const nextStep = useCallback(() => dispatch({ type: 'NEXT_STEP' }), [])
	const prevStep = useCallback(() => dispatch({ type: 'PREV_STEP' }), [])
	const skipTour = useCallback(() => dispatch({ type: 'SKIP_TOUR' }), [])
	const completeTour = useCallback(() => dispatch({ type: 'COMPLETE_TOUR' }), [])
	const markWelcomeSeen = useCallback(() => dispatch({ type: 'MARK_WELCOME_SEEN' }), [])
	const resetAllTours = useCallback(() => dispatch({ type: 'RESET_ALL' }), [])
	const isCompleted = useCallback(
		(tourId: string) => state.completedTours.includes(tourId),
		[state.completedTours],
	)

	const value = useMemo<OnboardingContextType>(
		() => ({
			state,
			startTour,
			nextStep,
			prevStep,
			skipTour,
			completeTour,
			markWelcomeSeen,
			resetAllTours,
			isCompleted,
		}),
		[state, startTour, nextStep, prevStep, skipTour, completeTour, markWelcomeSeen, resetAllTours, isCompleted],
	)

	return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
}

export function useOnboarding() {
	const context = useContext(OnboardingContext)
	if (!context) throw new Error('useOnboarding must be used within OnboardingProvider')
	return context
}
